import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './InputCalculator.css';
import PropTypes from 'prop-types';

/*istanbul ignore next */
const InputCalculator = ({
    value = '',
    onChange,
    placeholder = 'Enter value',
    disabled = false,
    onBlur,
    maxLength,
    className = '',
    percentMode = false,
    portal = false,
    safeBlur = false,
    ...props
}) => {
    const [showPopover, setShowPopover] = useState(false);
    const [calculatorValue, setCalculatorValue] = useState('');
    const [status, setStatus] = useState('');
    const [hasError, setHasError] = useState(false);
    const [popoverPos, setPopoverPos] = useState(null);

    const inputRef = useRef(null);
    const popoverRef = useRef(null);
    const fieldRef = useRef(null);

    // Update calculator value when popover opens
    useEffect(() => {
        if (showPopover) {
            setCalculatorValue(value || '');
        }
    }, [showPopover, value]);

    // Expression evaluator helper functions
    const isOperator = (char) => '+-*/%^()'.includes(char);

    const isNumber = (char) => /[0-9.]/.test(char);

    const parseNumber = (expr, startIndex) => {
        let endIndex = startIndex + 1;
        while (endIndex < expr.length && isNumber(expr[endIndex])) {
            endIndex++;
        }
        const num = expr.slice(startIndex, endIndex);
        if (num.split('.').length > 2) throw new Error('Invalid number');
        return { num, endIndex };
    };

    const isUnaryMinus = (tokens, index) => {
        return tokens[index] === '-' && (
            index === 0 ||
            ['(', '+', '-', '*', '/', '%', '^'].includes(tokens[index - 1])
        );
    };

    const processUnaryMinus = (tokens) => {
        const result = [];
        for (let i = 0; i < tokens.length; i++) {
            if (isUnaryMinus(tokens, i)) {
                result.push('u-');
            } else {
                result.push(tokens[i]);
            }
        }
        return result;
    };

    const shouldPopOperator = (top, current, precedence, rightAssoc) => {
        const precTop = precedence[top] ?? 0;
        const precCurrent = precedence[current] ?? 0;
        return (rightAssoc[current] && precCurrent < precTop) ||
            (!rightAssoc[current] && precCurrent <= precTop);
    };

    const processOperator = (token, operatorStack, outputQueue, precedence, rightAssoc) => {
        while (operatorStack.length > 0) {
            const top = operatorStack[operatorStack.length - 1];
            if (top === '(' || !shouldPopOperator(top, token, precedence, rightAssoc)) {
                break;
            }
            outputQueue.push(operatorStack.pop());
        }
        operatorStack.push(token);
    };

    const processClosingParen = (operatorStack, outputQueue) => {
        while (operatorStack.length && operatorStack[operatorStack.length - 1] !== '(') {
            outputQueue.push(operatorStack.pop());
        }
        if (operatorStack.pop() !== '(') {
            throw new Error('Mismatched parentheses');
        }
    };

    const executeBinaryOperation = (operator, a, b) => {
        switch (operator) {
            case '+': return a + b;
            case '-': return a - b;
            case '*': return a * b;
            case '/':
                if (b === 0) throw new Error('Division by zero');
                return a / b;
            case '%':
                if (b === 0) throw new Error('Division by zero');
                return a % b;
            case '^':
                return Math.pow(a, b);
            default:
                throw new Error('Unknown op: ' + operator);
        }
    };

    const tokenize = (expr) => {
        const tokens = [];
        let i = 0;

        while (i < expr.length) {
            const char = expr[i];

            if (char === ' ') {
                i++;
                continue;
            }

            if (isOperator(char)) {
                if (char === '×') {
                    tokens.push('*');
                } else if (char === '÷') {
                    tokens.push('/');
                } else {
                    tokens.push(char);
                }
                i++;
                continue;
            }

            if (isNumber(char)) {
                const { num, endIndex } = parseNumber(expr, i);
                tokens.push(num);
                i = endIndex;
                continue;
            }

            throw new Error('Unexpected character: ' + char);
        }

        return tokens;
    };

    const toRPN = (tokens) => {
        const outputQueue = [];
        const operatorStack = [];
        const precedence = { 'u-': 4, '^': 3, '*': 2, '/': 2, '%': 2, '+': 1, '-': 1 };
        const rightAssoc = { '^': true, 'u-': true };

        const processedTokens = processUnaryMinus(tokens);

        for (const token of processedTokens) {
            if (!isNaN(token)) { // NOSONAR
                outputQueue.push(parseFloat(token)); // NOSONAR
                continue;
            }

            if (token === 'u-' || '+-*/%^'.includes(token)) {
                processOperator(token, operatorStack, outputQueue, precedence, rightAssoc);
                continue;
            }

            if (token === '(') {
                operatorStack.push(token);
                continue;
            }

            if (token === ')') {
                processClosingParen(operatorStack, outputQueue);
                continue;
            }

            throw new Error('Bad token: ' + token);
        }

        while (operatorStack.length) {
            const operator = operatorStack.pop();
            if (operator === '(' || operator === ')') {
                throw new Error('Mismatched parentheses');
            }
            outputQueue.push(operator);
        }

        return outputQueue;
    };

    const evalRPN = (rpn) => {
        const stack = [];

        for (const token of rpn) {
            if (typeof token === 'number') {
                stack.push(token);
                continue;
            }

            if (token === 'u-') {
                const operand = stack.pop();
                if (operand === undefined) {
                    throw new Error('Syntax error');
                }
                stack.push(-operand);
                continue;
            }

            const b = stack.pop();
            const a = stack.pop();
            if (a === undefined || b === undefined) {
                throw new Error('Syntax error');
            }

            const result = executeBinaryOperation(token, a, b);
            stack.push(result);
        }

        if (stack.length !== 1) {
            throw new Error('Syntax error');
        }

        return stack[0];
    };

    // percentMode only: N% -> N/100; after + or -, N% is a share of the running total (1200 + 15% = 1380).
    const expandPercentToken = (out, groupStart) => {
        const prev = out[out.length - 1];
        if (prev === ')') {
            out.push('/', '100');
            return;
        }
        if (prev === undefined || isNaN(prev)) { // NOSONAR
            throw new Error('Syntax error');
        }

        out.pop();
        const operator = out[out.length - 1];
        const prefix = out.slice(groupStart, out.length - 1);
        if ((operator === '+' || operator === '-') && prefix.length > 0) {
            out.push('(', '(', ...prefix, ')', '*', prev, '/', '100', ')');
        } else {
            out.push('(', prev, '/', '100', ')');
        }
    };

    const applyPercentTokens = (tokens) => {
        const out = [];
        const groupStarts = [0];
        for (const token of tokens) {
            if (token === '%') {
                expandPercentToken(out, groupStarts[groupStarts.length - 1]);
                continue;
            }
            out.push(token);
            if (token === '(') {
                groupStarts.push(out.length);
            } else if (token === ')' && groupStarts.length > 1) {
                groupStarts.pop();
            }
        }
        return out;
    };

    const evaluate = (expr) => {
        const tokens = tokenize(expr);
        const rpn = toRPN(percentMode ? applyPercentTokens(tokens) : tokens);
        const val = evalRPN(rpn);
        // format to avoid floating point noise
        const rounded = Math.round((val + Number.EPSILON) * 1e12) / 1e12;
        return rounded;
    };

    // Event handlers
    const openPopover = useCallback(() => {
        if (!fieldRef.current) return;
        const rect = fieldRef.current.getBoundingClientRect();
        const popover = popoverRef.current;
        if (portal) {
            // Keep the 320px popover inside the viewport horizontally.
            const maxLeft = window.scrollX + document.documentElement.clientWidth - 320 - 8;
            setPopoverPos({
                left: Math.max(window.scrollX + 8, Math.min(rect.left + window.scrollX, maxLeft)),
                top: rect.bottom + 8 + window.scrollY,
            });
        } else if (popover) {
            popover.style.left = (rect.left + window.scrollX) + 'px';
            popover.style.top = (rect.bottom + 8 + window.scrollY) + 'px';
        }
        setShowPopover(true);
        setCalculatorValue(value || '');
        setStatus('');
    }, [value, portal]);

    const closePopover = () => {
        setShowPopover(false);
    };

    const handleToggle = () => {
        if (showPopover) {
            closePopover();
        } else {
            openPopover();
        }
    };

    const handleInputChange = (e) => {
        if (onChange) {
            onChange(e);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            try {
                const result = evaluate(value);
                if (onChange) {
                    onChange({ target: { value: String(result) } });
                }
                setStatus('');
                setHasError(false);
            } catch (err) {
                setStatus(err.message);
                setHasError(true);
                setTimeout(() => setHasError(false), 800);
            }
        } else if (e.key === 'Escape') {
            closePopover();
        }
    };

    const insertAtCalculatorCaret = (text) => {
        setCalculatorValue(prev => prev + text);
    };

    const handleCalculatorClick = (e) => {
        const btn = e.target.closest('button');
        if (!btn) return;

        const action = btn.getAttribute('data-action'); // NOSONAR
        const val = btn.getAttribute('data-val'); // NOSONAR

        if (action === 'clear') {
            setCalculatorValue('');
            setStatus('');
            return;
        }

        if (action === 'back') {
            setCalculatorValue(prev => prev.slice(0, -1));
            return;
        }

        if (action === 'eval') {
            try {
                const result = evaluate(calculatorValue);
                const resultString = String(result);

                // Update the main input value
                if (onChange) {
                    onChange({ target: { value: resultString } });
                }

                setStatus('');
                setHasError(false);

                // Close the calculator
                closePopover();

                // Focus back to the input
                inputRef.current?.focus();
            } catch (err) {
                setStatus(err.message);
                setHasError(true);
                setTimeout(() => setHasError(false), 800);
            }
            return;
        }

        if (val) {
            insertAtCalculatorCaret(val);
        }
    };

    // Click outside handler
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (
                showPopover &&
                popoverRef.current &&
                !popoverRef.current.contains(e.target) &&
                fieldRef.current &&
                !fieldRef.current.contains(e.target)
            ) {
                closePopover();
            }
        };

        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [showPopover]);

    // Window resize handler
    useEffect(() => {
        const handleResize = () => {
            if (showPopover) {
                openPopover();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showPopover, openPopover]);

    const handleInputBlur = (e) => {
        // Try to evaluate the expression on blur
        if (value?.trim()) {
            try {
                const result = evaluate(value);
                if (onChange) {
                    onChange({ target: { value: String(result) } });
                }
                setStatus('');
                setHasError(false);
            } catch (err) {
                // If evaluation fails, just keep the original value
                // You could optionally show an error status here
            }
        }

        // Call the original onBlur if provided
        if (onBlur) {
            let blurValue;
            try {
                blurValue = evaluate(value);
            } catch (err) {
                if (!safeBlur) throw err;
                // Keep the raw text so the caller's own validation can report it.
                blurValue = value;
            }
            onBlur({ ...e, target: { ...e.target, value: blurValue } });
        }
    };

    // A portal escapes ancestors with overflow (scrolling tables) that would clip the popover.
    const wrapPopover = (node) => (portal ? createPortal(node, document.body) : node);

    return (
        <div className="input-calculator-wrapper">
            <div className={`input-calculator-field ${className}`} ref={fieldRef}>
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    maxLength={maxLength}
                    className="form-control input-calculator-input"
                    autoComplete="off"
                    {...props}
                />
                <button
                    type="button"
                    className="input-calculator-toggle"
                    onClick={handleToggle}
                    disabled={disabled}
                    aria-label="Open calculator"
                    title="Open calculator"
                >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M7 2h10a3 3 0 0 1 3 3v14a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V5a3 3 0 0 1 3-3zm0 2a1 1 0 0 0-1 1v3h12V5a1 1 0 0 0-1-1H7zm11 7H6v7a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-7zM8.5 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3.5 0a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
                    </svg>
                </button>
            </div>

            {/* Calculator Popover */}
            {showPopover && wrapPopover(
                <div
                    className="input-calculator-popover"
                    ref={popoverRef}
                    style={portal && popoverPos ? { left: popoverPos.left, top: popoverPos.top, zIndex: 2000 } : undefined}
                    aria-modal="false"
                    aria-label="Calculator"
                >
                    <div className="calc-display">{calculatorValue || '\u00A0'}</div>
                    <fieldset 
                        className="calc-grid"
                        aria-label="Calculator buttons"
                    >
                        <button className="calc-btn calc-danger" data-action="clear" onClick={handleCalculatorClick}>AC</button>
                        <button className="calc-btn calc-danger" data-action="back" onClick={handleCalculatorClick}>⌫</button>
                        <button className="calc-btn calc-op" data-val="%" onClick={handleCalculatorClick}>%</button>
                        <button className="calc-btn calc-op" data-val="/" onClick={handleCalculatorClick}>÷</button>

                        <button className="calc-btn" data-val="7" onClick={handleCalculatorClick}>7</button>
                        <button className="calc-btn" data-val="8" onClick={handleCalculatorClick}>8</button>
                        <button className="calc-btn" data-val="9" onClick={handleCalculatorClick}>9</button>
                        <button className="calc-btn calc-op" data-val="*" onClick={handleCalculatorClick}>×</button>

                        <button className="calc-btn" data-val="4" onClick={handleCalculatorClick}>4</button>
                        <button className="calc-btn" data-val="5" onClick={handleCalculatorClick}>5</button>
                        <button className="calc-btn" data-val="6" onClick={handleCalculatorClick}>6</button>
                        <button className="calc-btn calc-op" data-val="-" onClick={handleCalculatorClick}>−</button>

                        <button className="calc-btn" data-val="1" onClick={handleCalculatorClick}>1</button>
                        <button className="calc-btn" data-val="2" onClick={handleCalculatorClick}>2</button>
                        <button className="calc-btn" data-val="3" onClick={handleCalculatorClick}>3</button>
                        <button className="calc-btn calc-op" data-val="+" onClick={handleCalculatorClick}>+</button>

                        <button className="calc-btn" data-val="(" onClick={handleCalculatorClick}>(</button>
                        <button className="calc-btn" data-val="0" onClick={handleCalculatorClick}>0</button>
                        <button className="calc-btn" data-val="." onClick={handleCalculatorClick}>.</button>
                        <button className="calc-btn calc-eq" data-action="eval" onClick={handleCalculatorClick}>=</button>
                        </fieldset>
                    <div className={`calc-status ${hasError ? 'error' : ''}`} aria-live="polite">
                        {status}
                    </div>
                </div>
            )}
        </div>
    );
};

InputCalculator.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    onBlur: PropTypes.func,
    maxLength: PropTypes.number,
    className: PropTypes.string,
    percentMode: PropTypes.bool,
    portal: PropTypes.bool,
    safeBlur: PropTypes.bool,
}

export default InputCalculator;